import { Col, Row, Tooltip } from 'antd';
import { useState } from 'react';
import { BsQuestionCircleFill } from 'react-icons/bs';
import { IoMdAddCircleOutline } from 'react-icons/io';
import { PiPencilSimpleBold } from 'react-icons/pi';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import Apis from '../../../../api';
import useIsTeamMember from '../../../../hooks/useIsTeamMember';
import { RootState } from '../../../../store/rootReducer';
import BackDrop from '../../../common/BackDrop';
import HSButton from '../../../common/Button';
import Confirmation from '../../../common/ConfirmationPopup';
import HSDropdown from '../../../common/Dropdown';
import InfoView from '../../../common/InfoView';
import NormalInput from '../../../common/NormalInput';
import loginActions from '../../../login/redux/actions';
import { BankAccount } from '../../../login/redux/types';
import msgActions from '../../../message/redux/actions';
import OnboardingCard from '../../onboarding/components/common/Card';

const BankInfoWrapper = () => {
    const dispatch = useDispatch();
    const isTeamMember = useIsTeamMember();

    const [visible, setVisible] = useState(false);
    const [loader, setLoader] = useState(false);
    const investorData = useSelector((state: RootState) => state.user.investor);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [onDeleteConfirm, setOnDeleteConfirm] = useState(false);

    const onSubmit = (ba: BankAccount) => {
        if (investorData) {
            setLoader(true);
            Apis.saveAndUpdateBankDetails(ba, investorData.uuid)
                .then((res) => {
                    dispatch(msgActions.showMessage({ text: 'Saved Successfully!', error: false }));
                    dispatch(loginActions.updateBankDetails(res.data.bankAccounts));
                    setVisible(false);
                })
                .catch((err) => {
                    dispatch(msgActions.showMessage({ text: String(err), error: true }));
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };
    const onDelete = (bankID: string) => {
        if (investorData) {
            setDeleteLoader(true);
            Apis.deleteBank(bankID, investorData.uuid)
                .then((res) => {
                    dispatch(msgActions.showMessage({ text: 'Deleted Successfully!', error: false }));
                    dispatch(loginActions.updateBankDetails(res.data.bankAccounts));
                })
                .catch((err) => {
                    dispatch(msgActions.showMessage({ text: String(err), error: true }));
                })
                .finally(() => {
                    setDeleteLoader(false);
                    setOnDeleteConfirm(false);
                });
        }
    };

    const bankAccounts = (investorData && investorData.bankAccounts) || [];

    if (!investorData || !investorData.bankAccounts) {
        return (
            <OnboardingCard>
                <div className="flex w-full pt-2">
                    {loader && <BackDrop />}
                    <BankDetailForm onCancel={() => setVisible(true)} onSubmit={onSubmit} />{' '}
                </div>
            </OnboardingCard>
        );
    }

    return (
        <>
            <OnboardingCard>
                <div className="flex flex-col w-full justify-center">
                    <div>
                        <div className="flex items-center  justify-between ">
                            {loader && <BackDrop />}
                            <div className="flex items-center ">
                                <p>Please provide your bank details</p>
                                <Tooltip title="We need this to be able to make any payments to you">
                                    <div className="ml-1 mr-4">
                                        <BsQuestionCircleFill />
                                    </div>
                                </Tooltip>
                            </div>
                            {!visible && !isTeamMember && (
                                <div
                                    className="w-9 h-9 md:w-10 md:h-10 rounded-full  cursor-pointer flex justify-center items-center bg-[#F5F6FF]"
                                    onClick={() => setVisible((x) => !x)}
                                >
                                    <IoMdAddCircleOutline className="text-xl md:text-2xl" color="#13974B" />
                                </div>
                            )}
                        </div>
                    </div>
                    {visible && (
                        <div className="pt-4">
                            <BankDetailForm onCancel={() => setVisible(false)} onSubmit={onSubmit} />
                        </div>
                    )}
                </div>
            </OnboardingCard>
            {bankAccounts.map((ba, i) => (
                <BankDetail
                    data={ba}
                    keyIndex={`${ba.bankAccountUUID}_${i}`}
                    key={ba.bankAccountUUID}
                    index={i + 1}
                    onDelete={onDelete}
                    onUpdate={onSubmit}
                    deleteLoader={deleteLoader}
                    onDeleteConfirm={onDeleteConfirm}
                    setOnDeleteConfirm={setOnDeleteConfirm}
                    isTeamMember={isTeamMember}
                />
            ))}
        </>
    );
};

export default BankInfoWrapper;

type BankDetailsProps = {
    data: BankAccount;
    index: number;
    onDelete: (bankID: string) => void;
    onUpdate: (data: BankAccount) => void;
    keyIndex: string;
    deleteLoader: boolean;
    onDeleteConfirm: boolean;
    setOnDeleteConfirm: (flag: boolean) => void;
    isTeamMember: boolean;
};
const BankDetail: React.FC<BankDetailsProps> = ({
    data,
    index,
    onDelete,
    onUpdate,
    keyIndex,
    deleteLoader,
    onDeleteConfirm,
    setOnDeleteConfirm,
    isTeamMember,
}) => {
    const [editing, setEditing] = useState(false);

    const keyValues = [
        {
            name: 'Account Currency',
            value: data.accountCurrency,
        },
        {
            name: 'Account Holder Name',
            value: data.accountName,
        },
        {
            name: 'Account Number',
            value: data.accountNumber,
        },
        {
            name: 'Bank Name',
            value: data.beneficiaryBank.bankName,
        },
        {
            name: 'SWIFT Code',
            value: data.beneficiaryBank.swiftCode,
        },
        {
            name: 'Bank Code',
            value: data.beneficiaryBank.bankCode,
        },
        {
            name: 'Branch Code',
            value: data.beneficiaryBank.branchCode,
        },
    ];

    return (
        <div className="mt-4" key={keyIndex}>
            <OnboardingCard>
                <div className="flex flex-col w-full">
                    <div className="flex items-center justify-between">
                        <p className="page_title_onboarding font-bold pt-0">Bank Account - {index}</p>
                        {!isTeamMember && (
                            <div className="flex">
                                {!editing && (
                                    <div
                                        className="w-9 h-9 md:w-10 md:h-10 rounded-full  cursor-pointer flex justify-center items-center bg-[#F5F6FF]"
                                        onClick={() => setEditing(true)}
                                    >
                                        <PiPencilSimpleBold className="text-xl md:text-2xl" color="#13974B" />
                                    </div>
                                )}
                                <div
                                    className="w-9 h-9 md:w-10 md:h-10 rounded-full ml-2  cursor-pointer flex justify-center items-center bg-[#F5F6FF]"
                                    onClick={() => setOnDeleteConfirm(true)}
                                >
                                    <RiDeleteBin5Line className="text-xl md:text-2xl" color="#13974B" />
                                </div>
                            </div>
                        )}
                    </div>
                    <div>
                        {editing ? (
                            <BankDetailForm
                                initialData={data}
                                onCancel={() => setEditing(false)}
                                onSubmit={(data) => {
                                    onUpdate(data);
                                    setEditing(false);
                                }}
                            />
                        ) : (
                            <Row gutter={[16, 16]}>
                                {keyValues.map((kv, index) => (
                                    <InfoView
                                        name={kv.name}
                                        value={kv.value}
                                        index={index}
                                        key={`${kv.name}_${index}`}
                                    />
                                ))}
                            </Row>
                        )}
                    </div>

                    <Confirmation
                        title="Delete Bank Account"
                        message={'Do you want to proceed with this action?'}
                        visible={onDeleteConfirm}
                        onCancel={() => setOnDeleteConfirm(false)}
                        onOk={() => {
                            onDelete(data.bankAccountUUID);
                        }}
                        loader={deleteLoader}
                    />
                </div>
            </OnboardingCard>
        </div>
    );
};

type FormProps = { onCancel: () => void; onSubmit: (ba: BankAccount) => void; initialData?: BankAccount };
const BankDetailForm: React.FC<FormProps> = ({ onCancel, onSubmit, initialData }) => {
    const isTeamMember: boolean = useIsTeamMember();
    const emptyObj: BankAccount = {
        accountCurrency: '',
        accountName: '',
        accountNumber: '',
        bankAccountUUID: '',
        beneficiaryBank: {
            bankCode: '',
            bankName: '',
            swiftCode: '',
            branchCode: undefined,
        },
    };
    const [formData, setFormData] = useState<BankAccount>(initialData || emptyObj);
    const [isValidate, setIsValidate] = useState(false);

    const currencies = [
        { label: 'SGD', value: 'SGD' },
        { label: 'USD', value: 'USD' },
        { label: 'Multi-CCY', value: 'Multi-CCY' },
    ];
    const onFinish = () => {
        if (isTeamMember) {
            return;
        }
        setIsValidate(true);
        if (
            !formData.accountCurrency ||
            formData.accountCurrency.trim() === '' ||
            !formData.accountName ||
            formData.accountName.trim() === '' ||
            !formData.accountNumber ||
            formData.accountNumber.trim() === '' ||
            !formData.beneficiaryBank?.bankCode ||
            formData.beneficiaryBank?.bankCode?.trim() === '' ||
            !formData.beneficiaryBank?.bankName ||
            formData.beneficiaryBank?.bankName?.trim() === '' ||
            !formData.beneficiaryBank?.swiftCode ||
            formData.beneficiaryBank?.swiftCode?.trim() === ''
        ) {
            return;
        }
        setIsValidate(false);
        onSubmit(formData);
        setFormData(emptyObj);
    };

    const onFieldChange = (data: { name: string; value?: number | string }) => {
        const { name, value } = data;
        let obj = {
            ...formData,
        };
        setFormData({ ...obj, [name]: value });
    };
    const isDisabled = isTeamMember;
    return (
        <div style={{ width: 'inherit' }}>
            <Row gutter={[16, 24]}>
                <Col xs={24} md={12}>
                    <HSDropdown
                        label="Account Currency"
                        name="accountCurrency"
                        options={currencies}
                        value={formData.accountCurrency}
                        onChange={onFieldChange}
                        required
                        className="rounded-xl"
                        disabled={isDisabled}
                    />
                    {isValidate && (!formData.accountCurrency || formData.accountCurrency.trim() === '') && (
                        <span className="common_error absolute">Please select account currency</span>
                    )}
                </Col>
                <Col xs={24} md={12}>
                    <NormalInput
                        title="Account Holder Name"
                        name="accountName"
                        value={formData.accountName}
                        onChange={onFieldChange}
                        className="rounded-xl"
                        isRequired
                        disabled={isDisabled}
                    />
                    {isValidate && (!formData.accountName || formData.accountName.trim() === '') && (
                        <span className="common_error absolute">Please enter account holder name</span>
                    )}
                </Col>
                <Col xs={24} md={12}>
                    <NormalInput
                        title="Account Number"
                        name="accountNumber"
                        value={formData.accountNumber}
                        onChange={onFieldChange}
                        className="rounded-xl"
                        isRequired
                        disabled={isDisabled}
                    />
                    {isValidate && (!formData.accountNumber || formData.accountNumber.trim() === '') && (
                        <span className="common_error absolute">Please enter account number</span>
                    )}
                </Col>
                <Col xs={24} md={12}>
                    <NormalInput
                        title="Bank Name"
                        name="bankName"
                        value={formData.beneficiaryBank.bankName}
                        onChange={(data: { name: string; value?: number | string }) => {
                            let form = { ...formData };
                            let beneficiaryBank = { ...form.beneficiaryBank };
                            beneficiaryBank.bankName = String(data.value || '');
                            form.beneficiaryBank = beneficiaryBank;
                            setFormData(form);
                        }}
                        className="rounded-xl"
                        isRequired
                        disabled={isDisabled}
                    />
                    {isValidate &&
                        (!formData.beneficiaryBank?.bankName || formData.beneficiaryBank?.bankName?.trim() === '') && (
                            <span className="common_error absolute">Please enter bank name</span>
                        )}
                </Col>
                <Col xs={24} md={12}>
                    <NormalInput
                        title="Swift Code"
                        name="swiftCode"
                        value={formData.beneficiaryBank.swiftCode}
                        onChange={(data: { name: string; value?: number | string }) => {
                            let form = { ...formData };
                            let beneficiaryBank = { ...form.beneficiaryBank };
                            beneficiaryBank.swiftCode = String(data.value || '');
                            form.beneficiaryBank = beneficiaryBank;
                            setFormData(form);
                        }}
                        className="rounded-xl"
                        isRequired
                        disabled={isDisabled}
                    />
                    {isValidate &&
                        (!formData.beneficiaryBank?.swiftCode ||
                            formData.beneficiaryBank?.swiftCode?.trim() === '') && (
                            <span className="common_error absolute">Please enter swift code</span>
                        )}
                </Col>
                <Col xs={24} md={12}>
                    <NormalInput
                        title="Bank Code"
                        name="bankCode"
                        value={formData.beneficiaryBank.bankCode}
                        onChange={(data: { name: string; value?: number | string }) => {
                            let form = { ...formData };
                            let beneficiaryBank = { ...form.beneficiaryBank };
                            beneficiaryBank.bankCode = String(data.value || '');
                            form.beneficiaryBank = beneficiaryBank;
                            setFormData(form);
                        }}
                        className="rounded-xl"
                        isRequired
                        disabled={isDisabled}
                    />
                    {isValidate &&
                        (!formData.beneficiaryBank?.bankCode || formData.beneficiaryBank?.bankCode?.trim() === '') && (
                            <span className="common_error absolute">Please enter bank code</span>
                        )}
                </Col>
                <Col xs={24} md={12}>
                    <NormalInput
                        title="Branch Code"
                        name="branchCode"
                        value={formData.beneficiaryBank.branchCode}
                        onChange={(data: { name: string; value?: number | string }) => {
                            let form = { ...formData };
                            let beneficiaryBank = { ...form.beneficiaryBank };
                            beneficiaryBank.branchCode = String(data.value || '');
                            form.beneficiaryBank = beneficiaryBank;
                            setFormData(form);
                        }}
                        className="rounded-xl"
                        disabled={isDisabled}
                    />
                </Col>
            </Row>
            {!isTeamMember ? (
                <div className="flex justify-center items-center mt-10 mb-5">
                    <HSButton title={'Submit'} onClick={onFinish} type="primary" disabled={isTeamMember} />
                    <HSButton className="onboarding_btn ml-2 " light title="Cancel" onClick={onCancel} />
                </div>
            ) : null}
        </div>
    );
};
